import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTenant } from "../../context/TenantContext";
import umService from "../../services/university-admin/umService";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";

const UMUserForm = ({ user, onSubmit, onCancel, loading }) => {
  const { t } = useTranslation(["users", "common"]);
  const { t: tErrors } = useTranslation("errors");
  const { userRole, currentTenantId, universities } = useTenant();
  const { can } = useUniAdminAuth();
  const canListCampuses = can(PERMISSIONS.campus.list);
  const canListFaculties = can(PERMISSIONS.faculty.list);
  const canListDepartments = can(PERMISSIONS.department.list);

  const tenantCtx = { userRole, currentTenantId };

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    studentNumber: "",
    phoneNumber: "",
    gender: "MALE",
    campusId: "",
    universityId: "",
    facultyId: "",
    departmentId: "",
    educationLevel: 1,
  });

  const [dropdownData, setDropdownData] = useState({
    universities: [],
    campuses: [],
    faculties: [],
    departments: [],
  });

  const [errors, setErrors] = useState({});

  const scopedUniversities = useMemo(() => {
    if (userRole === "UNIVERSITY_MANAGER" && currentTenantId) {
      return universities.filter((item) => Number(item.id) === Number(currentTenantId));
    }
    return universities;
  }, [universities, userRole, currentTenantId]);

  const genderOptions = [
    { value: "MALE", label: t("users:gender.MALE") },
    { value: "FEMALE", label: t("users:gender.FEMALE") },
    { value: "OTHER", label: t("users:gender.OTHER") },
  ];

  const educationLevelOptions = [
    { value: 1, label: t("users:educationLevel.1") },
    { value: 2, label: t("users:educationLevel.2") },
    { value: 3, label: t("users:educationLevel.3") },
    { value: 4, label: t("users:educationLevel.4") },
    { value: 5, label: t("users:educationLevel.5") },
    { value: 6, label: t("users:educationLevel.6") },
    { value: 7, label: t("users:educationLevel.7") },
    { value: 8, label: t("users:educationLevel.8") },
    { value: 9, label: t("users:educationLevel.9") },
  ];

  useEffect(() => {
    setDropdownData((prev) => ({ ...prev, universities: scopedUniversities }));
  }, [scopedUniversities]);

  useEffect(() => {
    if (!user) {
      if (userRole === "UNIVERSITY_MANAGER" && currentTenantId) {
        setFormData((prev) => ({ ...prev, universityId: String(currentTenantId) }));
      }
      return;
    }

    setFormData({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      password: "",
      studentNumber: user.studentNumber || "",
      phoneNumber: user.phoneNumber || "",
      gender: user.gender || "MALE",
      campusId: user.campusId ? String(user.campusId) : "",
      universityId: user.universityId ? String(user.universityId) : "",
      facultyId: user.facultyId ? String(user.facultyId) : "",
      departmentId: user.departmentId ? String(user.departmentId) : "",
      educationLevel: Number(user.educationLevel || 1),
    });
  }, [user, userRole, currentTenantId]);

  useEffect(() => {
    if (!canListCampuses) {
      setDropdownData((prev) => ({ ...prev, campuses: [] }));
      return;
    }
    umService.getAllCampuses(tenantCtx).then((response) => {
      if (!response.success) {
        setDropdownData((prev) => ({ ...prev, campuses: [] }));
        return;
      }

      const campuses = (response.data || []).filter(
        (item) => !formData.universityId || Number(item.universityId) === Number(formData.universityId)
      );

      setDropdownData((prev) => ({ ...prev, campuses }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.universityId, userRole, currentTenantId, canListCampuses]);

  useEffect(() => {
    if (!canListFaculties) {
      setDropdownData((prev) => ({ ...prev, faculties: [] }));
      return;
    }
    umService.getAllFaculties(tenantCtx).then((response) => {
      if (!response.success) {
        setDropdownData((prev) => ({ ...prev, faculties: [] }));
        return;
      }

      const faculties = (response.data || []).filter(
        (item) => !formData.campusId || Number(item.campusId) === Number(formData.campusId)
      );

      setDropdownData((prev) => ({ ...prev, faculties }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.campusId, userRole, currentTenantId, canListFaculties]);

  useEffect(() => {
    if (!formData.facultyId || !canListDepartments) {
      setDropdownData((prev) => ({ ...prev, departments: [] }));
      return;
    }

    umService
      .getDepartments({ ...tenantCtx, facultyId: Number(formData.facultyId), page: 0, size: 1000 })
      .then((response) => {
        setDropdownData((prev) => ({ ...prev, departments: response.success ? response.data.content || [] : [] }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.facultyId, userRole, currentTenantId, canListDepartments]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      if (name === "universityId") {
        updated.campusId = "";
        updated.facultyId = "";
        updated.departmentId = "";
      }

      if (name === "campusId") {
        updated.facultyId = "";
        updated.departmentId = "";
      }

      if (name === "facultyId") {
        updated.departmentId = "";
      }

      return updated;
    });

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.firstName.trim()) nextErrors.firstName = t("users:validation.firstNameRequired");
    if (!formData.lastName.trim()) nextErrors.lastName = t("users:validation.lastNameRequired");

    if (!formData.email.trim()) {
      nextErrors.email = t("users:validation.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = t("users:validation.emailInvalid");
    }

    if (!user && !formData.password.trim()) {
      nextErrors.password = t("users:validation.passwordRequired");
    } else if (!user && formData.password.length < 8) {
      nextErrors.password = t("users:validation.passwordMinLength");
    }

    if (!formData.studentNumber.trim()) nextErrors.studentNumber = t("users:validation.studentNumberRequired");
    if (!(userRole === "UNIVERSITY_MANAGER" && Boolean(currentTenantId)) && !formData.universityId) {
      nextErrors.universityId = t("users:validation.universityRequired");
    }
    if (!formData.campusId) nextErrors.campusId = t("users:validation.campusRequired");

    if (formData.phoneNumber && !/^\+?[\d\s\-()+]+$/.test(formData.phoneNumber)) {
      nextErrors.phoneNumber = t("users:validation.phoneInvalid");
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const submitData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      studentNumber: formData.studentNumber.trim(),
      phoneNumber: formData.phoneNumber.trim(),
      gender: formData.gender,
      campusId: Number(formData.campusId),
      universityId: Number(formData.universityId),
      facultyId: formData.facultyId ? Number(formData.facultyId) : null,
      departmentId: formData.departmentId ? Number(formData.departmentId) : null,
      educationLevel: Number(formData.educationLevel),
    };

    if (!user && formData.password) {
      submitData.password = formData.password;
    }

    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="user-form">
      <div className="row g-4">
        <div className="col-12">
          <div className="form-section">
            <div className="form-section-header">
              <i className="fas fa-user"></i>
              <h6>{t("users:form.personalInfo")}</h6>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-bold">{t("users:fields.firstName")} <span className="text-danger">*</span></label>
                <input
                  type="text"
                  name="firstName"
                  className={`form-control ${errors.firstName ? "is-invalid" : ""}`}
                  placeholder={t("users:placeholders.firstName")}
                  value={formData.firstName}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
              </div>

              <div className="col-md-6">
                <label className="form-label fw-bold">{t("users:fields.lastName")} <span className="text-danger">*</span></label>
                <input
                  type="text"
                  name="lastName"
                  className={`form-control ${errors.lastName ? "is-invalid" : ""}`}
                  placeholder={t("users:placeholders.lastName")}
                  value={formData.lastName}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
              </div>

              <div className="col-md-6">
                <label className="form-label fw-bold">{t("users:fields.email")} <span className="text-danger">*</span></label>
                <input
                  type="email"
                  name="email"
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  placeholder={t("users:placeholders.email")}
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
              </div>

              {!user && (
                <div className="col-md-6">
                  <label className="form-label fw-bold">{t("users:fields.password")} <span className="text-danger">*</span></label>
                  <input
                    type="password"
                    name="password"
                    className={`form-control ${errors.password ? "is-invalid" : ""}`}
                    placeholder={t("users:placeholders.password")}
                    value={formData.password}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                  {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                </div>
              )}

              <div className="col-md-6">
                <label className="form-label fw-bold">{t("users:fields.studentNumber")} <span className="text-danger">*</span></label>
                <input
                  type="text"
                  name="studentNumber"
                  className={`form-control ${errors.studentNumber ? "is-invalid" : ""}`}
                  placeholder={t("users:placeholders.studentNumber")}
                  value={formData.studentNumber}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                {errors.studentNumber && <div className="invalid-feedback">{errors.studentNumber}</div>}
              </div>

              <div className="col-md-6">
                <label className="form-label fw-bold">{t("users:fields.phoneNumber")}</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  className={`form-control ${errors.phoneNumber ? "is-invalid" : ""}`}
                  placeholder={t("users:placeholders.phoneNumber")}
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                {errors.phoneNumber && <div className="invalid-feedback">{errors.phoneNumber}</div>}
              </div>

              <div className="col-md-6">
                <label className="form-label fw-bold">{t("users:fields.gender")}</label>
                <select name="gender" className="form-select" value={formData.gender} onChange={handleInputChange} disabled={loading}>
                  {genderOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label fw-bold">{t("users:fields.educationLevel")}</label>
                <select
                  name="educationLevel"
                  className="form-select"
                  value={formData.educationLevel}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  {educationLevelOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="form-section">
            <div className="form-section-header">
              <i className="fas fa-building"></i>
              <h6>{t("users:form.academicInfo")}</h6>
            </div>

            <div className="row g-3">
              {/* University dropdown is hidden for uni-admin users — their university is fixed and set automatically */}
              {!(userRole === "UNIVERSITY_MANAGER" && Boolean(currentTenantId)) && (
                <div className="col-md-6">
                  <label className="form-label fw-bold">{t("users:fields.university")} <span className="text-danger">*</span></label>
                  <select
                    name="universityId"
                    className={`form-select ${errors.universityId ? "is-invalid" : ""}`}
                    value={formData.universityId}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="">{t("users:placeholders.selectUniversity")}</option>
                    {dropdownData.universities.map((university) => (
                      <option key={university.id} value={university.id}>{university.nameEn}</option>
                    ))}
                  </select>
                  {errors.universityId && <div className="invalid-feedback">{errors.universityId}</div>}
                </div>
              )}

              <div className="col-md-6">
                <label className="form-label fw-bold">{t("users:fields.campus")} <span className="text-danger">*</span></label>
                <select
                  name="campusId"
                  className={`form-select ${errors.campusId ? "is-invalid" : ""}`}
                  value={formData.campusId}
                  onChange={handleInputChange}
                  disabled={loading || !canListCampuses}
                >
                  <option value="">{t("users:placeholders.selectCampus")}</option>
                  {dropdownData.campuses.map((campus) => (
                    <option key={campus.id} value={campus.id}>{campus.nameEn}</option>
                  ))}
                </select>
                {errors.campusId && <div className="invalid-feedback">{errors.campusId}</div>}
                {!canListCampuses && <small className="text-danger">{tErrors("permission.denied")}</small>}
              </div>

              <div className="col-md-6">
                <label className="form-label fw-bold">{t("users:fields.faculty")}</label>
                <select
                  name="facultyId"
                  className="form-select"
                  value={formData.facultyId}
                  onChange={handleInputChange}
                  disabled={loading || !canListFaculties}
                >
                  <option value="">{t("users:placeholders.selectFaculty")}</option>
                  {dropdownData.faculties.map((faculty) => (
                    <option key={faculty.id} value={faculty.id}>{faculty.nameEn}</option>
                  ))}
                </select>
                {!canListFaculties && <small className="text-danger">{tErrors("permission.denied")}</small>}
              </div>

              <div className="col-md-6">
                <label className="form-label fw-bold">{t("users:fields.department")}</label>
                <select
                  name="departmentId"
                  className="form-select"
                  value={formData.departmentId}
                  onChange={handleInputChange}
                  disabled={loading || !canListDepartments}
                >
                  <option value="">{t("users:placeholders.selectDepartment")}</option>
                  {dropdownData.departments.map((department) => (
                    <option key={department.id} value={department.id}>{department.nameEn}</option>
                  ))}
                </select>
                {!canListDepartments && <small className="text-danger">{tErrors("permission.denied")}</small>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
        <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={loading}>
          {t("users:form.cancel")}
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? t("users:form.saving") : user ? t("users:form.updateUser") : t("users:form.createUser")}
        </button>
      </div>
    </form>
  );
};

export default UMUserForm;
